import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';

import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import GetAppIcon from '@material-ui/icons/GetApp';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import PersonIcon from '@material-ui/icons/Person';
import DateRangeIcon from '@material-ui/icons/DateRange';
import EditIcon from '@material-ui/icons/Edit';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { C } from './constants';
import { empMeta, initialOf, displayName } from './employeeColors';
import { weekdayOf, labelOf, timeOf } from './pointsWatchingHelpers';

// ---- Avatar ---------------------------------------------------------------
export function Avatar({ name, idKey, color, size = 44 }) {
    const meta = empMeta(idKey, name, color);
    return (
        <span
            style={{
                display: 'flex',
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontWeight: 700,
                width: size,
                height: size,
                backgroundColor: meta.fill,
                color: meta.ink,
                fontSize: size * 0.4,
            }}
        >
            {initialOf(name)}
        </span>
    );
}

// ---- Status pill ----------------------------------------------------------
export function StatusTag({ status }) {
    const live = status === 'In progress';
    return (
        <span
            style={{
                borderRadius: 999,
                border: '1px solid',
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                ...(live
                    ? { backgroundColor: '#d6f5e3', color: '#157a4a', borderColor: '#9be0bb' }
                    : { backgroundColor: '#eef1f8', color: '#5b6478', borderColor: '#dadff0' }),
            }}
        >
            {live ? 'IN PROGRESS' : 'CLOSED'}
        </span>
    );
}

// ---- Employee tag (the "Given by" pill) -----------------------------------
export function EmployeeTag({ userId, name, color, onClick }) {
    const meta = empMeta(userId, name, color);
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick(userId, name);
            }}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                borderRadius: 999,
                border: 'none',
                cursor: onClick ? 'pointer' : 'default',
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: meta.fill,
                color: meta.ink,
            }}
        >
            <span style={{ height: 8, width: 8, borderRadius: '50%', backgroundColor: meta.ink }} />
            {displayName(name)}
        </button>
    );
}

// ---- Photo frame ----------------------------------------------------------
export function PhotoFrame({ url, kind, unclear, onExpand }) {
    const isId = kind === 'id';
    const showUnclear = !!unclear;
    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%', // square
                    overflow: 'hidden',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg,#1b2452 0%,#2d3a73 45%,#3a2f5e 100%)',
                }}
            >
                {url && !showUnclear ? (
                    <img
                        src={url}
                        alt={isId ? 'ID' : 'Points'}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            backgroundColor: showUnclear ? 'rgba(2,6,23,0.5)' : 'transparent',
                        }}
                    >
                        {showUnclear ? (
                            <>
                                <BrokenImageIcon style={{ color: '#fcd34d', fontSize: 28 }} />
                                <span
                                    style={{
                                        padding: '0 8px',
                                        textAlign: 'center',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#fde68a',
                                    }}
                                >
                                    No clear subject
                                </span>
                            </>
                        ) : isId ? (
                            <PersonIcon style={{ color: 'rgba(255,255,255,0.7)', fontSize: 38 }} />
                        ) : (
                            <CameraAltIcon style={{ color: 'rgba(255,255,255,0.6)', fontSize: 34 }} />
                        )}
                    </div>
                )}

                {url && (
                    <IconButton
                        size="small"
                        onClick={() => onExpand && onExpand(url, isId ? 'ID Picture' : 'Points Picture')}
                        style={{
                            position: 'absolute',
                            right: 6,
                            top: 6,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            color: 'rgba(255,255,255,0.9)',
                            padding: 4,
                        }}
                    >
                        <ZoomOutMapIcon style={{ fontSize: 14 }} />
                    </IconButton>
                )}
            </div>
            <p style={{ margin: '4px 0 0', textAlign: 'center', fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                {isId ? 'ID Picture' : 'Points Picture'}
            </p>
        </div>
    );
}

// ---- Source tag (how the points were given: manual vs phone) --------------
export function SourceTag({ source }) {
    if (!source) return null;
    const isManual = String(source).toLowerCase() === 'manual';
    const label = isManual ? 'Manual' : 'By phone';
    const Icon = isManual ? EditIcon : PhoneIphoneIcon;
    const palette = isManual
        ? { backgroundColor: '#eef1f8', color: '#5b6478', borderColor: '#dadff0' }
        : { backgroundColor: '#d6f5e3', color: '#157a4a', borderColor: '#9be0bb' };
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                borderRadius: 999,
                border: '1px solid',
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
                ...palette,
            }}
        >
            <Icon style={{ fontSize: 12 }} /> {label}
        </span>
    );
}

// ---- Entry card -----------------------------------------------------------
export function EntryCard({ e, onPickBy, showBy = true, onExpand, onClearAlert }) {
    // Active alert = points-time capture missing AND not yet dismissed by a
    // reviewer. Once cleared, the amber flag and the Clear button both drop.
    const alertActive = !!e.noClearSubject && !e.alertCleared;
    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: 16,
                border: alertActive ? '1px solid #fcd34d' : '1px solid #e2e8f0',
                backgroundColor: '#fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '16px 16px 0' }}>
                <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 16, fontWeight: 700, color: C.navy2 }}>
                        {e.customerName}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
                        {timeOf(e.signInTime)}{e.machine ? ` · Machine ${e.machine}` : ''}
                    </p>
                </div>
                <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: 8 }}>
                    <SourceTag source={e.source} />
                    <span style={{ borderRadius: 8, padding: '4px 10px', fontSize: 14, fontWeight: 700, backgroundColor: C.navy2, color: C.yellow }}>
                        +{e.points}
                    </span>
                </div>
            </div>

            {showBy && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 0' }}>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8' }}>Given by</span>
                    <EmployeeTag userId={e.userId} name={e.employeeName} color={e.employeeColor} onClick={onPickBy} />
                </div>
            )}

            <div style={{ display: 'flex', gap: 12, padding: '12px 16px 16px' }}>
                <PhotoFrame url={e.pointsPicture} kind="points" unclear={alertActive} onExpand={onExpand} />
                <PhotoFrame url={e.idPicture} kind="id" onExpand={onExpand} />
            </div>

            {alertActive && onClearAlert && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        borderTop: '1px solid #fef3c7',
                        backgroundColor: '#fffbeb',
                        padding: '10px 16px',
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#b45309' }}>
                        <BrokenImageIcon style={{ fontSize: 16 }} /> No clear subject
                    </span>
                    <button
                        type="button"
                        onClick={() => onClearAlert(e)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            borderRadius: 8,
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px 12px',
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#fff',
                            backgroundColor: '#157a4a',
                        }}
                    >
                        <CheckCircleIcon style={{ fontSize: 16 }} /> Clear alert
                    </button>
                </div>
            )}
        </div>
    );
}

// ---- Employee day card ----------------------------------------------------
export function EmployeeDayCard({ d, onClick }) {
    const points = (d.pointsGiven != null ? Number(d.pointsGiven) : 0).toLocaleString();
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                width: '100%',
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                backgroundColor: '#fff',
                padding: 16,
                textAlign: 'left',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                cursor: 'pointer',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ display: 'flex', height: 44, width: 44, flexShrink: 0, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: C.track, color: C.ink }}>
                    <DateRangeIcon style={{ fontSize: 20 }} />
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 16, fontWeight: 700, color: C.navy2 }}>
                            {weekdayOf(d.date)}, {labelOf(d.date)}
                        </span>
                        <StatusTag status={d.status} />
                    </div>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
                        {d.sessionNumber ? `Session ${d.sessionNumber} · ` : ''}{d.shift || ''}
                    </p>
                </div>
                <ChevronRightIcon style={{ flexShrink: 0, color: '#cbd5e1' }} />
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                <div>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8' }}>Points given</p>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.navy2 }}>{points}</p>
                </div>
                <div>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8' }}>Entries</p>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.navy2 }}>{d.entries}</p>
                </div>
            </div>
        </button>
    );
}

// ---- Summary banner -------------------------------------------------------
export function Banner({ left, avatarKey, avatarName, avatarColor, count, unit }) {
    return (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 16, padding: '16px 20px', color: '#fff', backgroundColor: C.navy2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {avatarName && <Avatar name={avatarName} idKey={avatarKey} color={avatarColor} size={40} />}
                <div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: C.periInk }}>Showing</p>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{left}</p>
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, lineHeight: 1, color: C.yellow }}>{count}</p>
                <p style={{ margin: 0, fontSize: 12, color: C.periInk }}>{unit}</p>
            </div>
        </div>
    );
}

// ---- Empty prompt ---------------------------------------------------------
export function EmptyPrompt({ icon, title, text }) {
    return (
        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 32px', textAlign: 'center' }}>
            <div style={{ display: 'flex', height: 64, width: 64, alignItems: 'center', justifyContent: 'center', borderRadius: 16, backgroundColor: C.track }}>
                {icon}
            </div>
            <p style={{ margin: '16px 0 0', fontSize: 16, fontWeight: 600, color: C.navy2 }}>{title}</p>
            <p style={{ margin: '4px 0 0', maxWidth: 460, fontSize: 14, color: '#64748b' }}>{text}</p>
        </div>
    );
}

// ---- Search box -----------------------------------------------------------
export function SearchBox({ query, setQuery }) {
    return (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, borderRadius: 12, border: '1px solid #e2e8f0', padding: '10px 12px' }}>
            <SearchIcon style={{ fontSize: 18, color: '#94a3b8' }} />
            <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search customer or machine"
                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: 14 }}
            />
            {query && (
                <button type="button" onClick={() => setQuery('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
                    <CloseIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                </button>
            )}
        </div>
    );
}

// ---- Download PDF button --------------------------------------------------
export function DownloadBtn({ onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="pw-download-btn"
            style={{
                marginTop: 4,
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderRadius: 12,
                border: 'none',
                padding: '14px 0',
                fontSize: 16,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: C.navy2,
                cursor: 'pointer',
            }}
        >
            <GetAppIcon style={{ fontSize: 20 }} /> Download PDF
        </button>
    );
}

// ---- Alerts panel ---------------------------------------------------------
export function NotifPanel({ open, onClose, items, onPick }) {
    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <div style={{ width: 'min(420px, 100vw)', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', backgroundColor: C.navy }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <NotificationsNoneIcon style={{ color: C.yellow, fontSize: 20 }} />
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Alerts</span>
                        {items.length > 0 && (
                            <span style={{ borderRadius: 999, backgroundColor: C.alertRed, padding: '2px 8px', fontSize: 12, fontWeight: 700, color: '#fff' }}>
                                {items.length}
                            </span>
                        )}
                    </div>
                    <IconButton size="small" onClick={onClose} style={{ color: 'rgba(255,255,255,0.8)' }}>
                        <CloseIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </div>
                <p style={{ margin: 0, borderBottom: '1px solid #f1f5f9', padding: '8px 20px', fontSize: 12, color: '#64748b' }}>
                    Entries where the camera captured no clear face
                </p>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {items.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 32px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', height: 56, width: 56, alignItems: 'center', justifyContent: 'center', borderRadius: 16, backgroundColor: '#eafaf0' }}>
                                <NotificationsNoneIcon style={{ color: '#157a4a', fontSize: 28 }} />
                            </div>
                            <p style={{ margin: '12px 0 0', fontSize: 16, fontWeight: 600, color: C.navy2 }}>All clear</p>
                            <p style={{ margin: '4px 0 0', fontSize: 14, color: '#64748b' }}>No alerts right now.</p>
                        </div>
                    ) : (
                        items.map((n) => {
                            const meta = empMeta(n.userId, n.employeeName);
                            return (
                                <button
                                    key={n.pendingId}
                                    type="button"
                                    onClick={() => onPick(n)}
                                    style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, border: 'none', borderBottom: '1px solid #f1f5f9', background: 'transparent', padding: '12px 20px', textAlign: 'left', cursor: 'pointer' }}
                                >
                                    <span style={{ display: 'flex', height: 36, width: 36, flexShrink: 0, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: C.amberBg }}>
                                        <BrokenImageIcon style={{ fontSize: 18, color: C.amberIcon }} />
                                    </span>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.navy2 }}>No clear subject</p>
                                        <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>
                                            <span style={{ fontWeight: 600, color: meta.ink }}>{displayName(n.employeeName)}</span>
                                            {n.machine ? ` · Machine ${n.machine}` : ''} · {timeOf(n.signInTime)}
                                        </p>
                                        <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{weekdayOf(n.signInDate)}, {labelOf(n.signInDate)}</p>
                                    </div>
                                    <ChevronRightIcon style={{ flexShrink: 0, color: '#cbd5e1' }} />
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </Drawer>
    );
}

// ---- Photo lightbox -------------------------------------------------------
export function Lightbox({ open, src, label, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <div style={{ position: 'relative', backgroundColor: '#0f1640' }}>
                <IconButton
                    size="small"
                    onClick={onClose}
                    style={{ position: 'absolute', right: 8, top: 8, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                >
                    <CloseIcon />
                </IconButton>
                {src && (
                    <img src={src} alt={label || 'photo'} style={{ display: 'block', maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }} />
                )}
                {label && (
                    <p style={{ margin: 0, padding: '8px 16px', textAlign: 'center', color: '#fff', fontSize: 13 }}>{label}</p>
                )}
            </div>
        </Dialog>
    );
}
